// engine/audio.ts — every sound in the game is synthesised here. No audio assets.
// SFX are envelope + oscillator/noise recipes; music is a step sequencer driven
// from the render frame with a lookahead window, with the percussion layer gated
// by combat state (RESEARCH: silence-then-drums reads as danger without a stinger).

import type { MusicParams } from '../types'

type SfxName =
  | 'swing'
  | 'hit'
  | 'kill'
  | 'pickup'
  | 'door'
  | 'generator-die'
  | 'player-hurt'
  | 'dash'
  | 'potion'
  | 'telegraph'
  | 'shoot'
  | 'death'
  | 'victory'
  | 'ui'

const MODES: Record<MusicParams['mode'], number[]> = {
  aeolian: [0, 2, 3, 5, 7, 8, 10],
  dorian: [0, 2, 3, 5, 7, 9, 10],
  phrygian: [0, 1, 3, 5, 7, 8, 10],
  lydian: [0, 2, 4, 6, 7, 9, 11],
  harmonicMinor: [0, 2, 3, 5, 7, 8, 11],
}

function midiToFreq(m: number): number {
  return 440 * Math.pow(2, (m - 69) / 12)
}

class AudioEngine {
  private ctx: AudioContext | null = null
  private master: GainNode | null = null
  private musicBus: GainNode | null = null
  private sfxBus: GainNode | null = null
  private noise: AudioBuffer | null = null
  private ready = false
  private failed = false

  volume = 0.7
  muted = false

  // --- music state
  private params: MusicParams | null = null
  private nextStepTime = 0
  private step = 0
  private combat = false
  private percGain = 0
  private musicOn = false

  init(): void {
    if (this.ready || this.failed) return
    try {
      const Ctor: typeof AudioContext =
        window.AudioContext ?? (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext
      if (!Ctor) {
        this.failed = true
        return
      }
      const ctx = new Ctor()
      this.ctx = ctx
      this.master = ctx.createGain()
      this.master.gain.value = this.volume
      this.master.connect(ctx.destination)
      this.musicBus = ctx.createGain()
      this.musicBus.gain.value = 0.55
      this.musicBus.connect(this.master)
      this.sfxBus = ctx.createGain()
      this.sfxBus.gain.value = 0.9
      this.sfxBus.connect(this.master)

      // one shared white-noise buffer; never re-allocated
      const len = Math.floor(ctx.sampleRate * 1.0)
      const buf = ctx.createBuffer(1, len, ctx.sampleRate)
      const d = buf.getChannelData(0)
      for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1
      this.noise = buf
      this.ready = true
    } catch {
      this.failed = true
    }
  }

  /** Browsers require a gesture; wired to the first real key/mouse event. */
  unlock(): void {
    this.init()
    try {
      if (this.ctx && this.ctx.state !== 'running') void this.ctx.resume()
    } catch {
      /* ignore */
    }
  }

  setVolume(v: number): void {
    this.volume = Math.max(0, Math.min(1, v))
    if (this.master) this.master.gain.value = this.muted ? 0 : this.volume
  }

  // ------------------------------------------------------------- primitives

  private t(): number {
    return this.ctx ? this.ctx.currentTime : 0
  }

  private tone(
    type: OscillatorType,
    f0: number,
    f1: number,
    dur: number,
    peak: number,
    attack = 0.005,
    dest?: AudioNode,
    when?: number,
  ): void {
    const ctx = this.ctx
    if (!ctx) return
    const t0 = when ?? this.t()
    const osc = ctx.createOscillator()
    const g = ctx.createGain()
    osc.type = type
    osc.frequency.setValueAtTime(Math.max(20, f0), t0)
    if (f1 !== f0) osc.frequency.exponentialRampToValueAtTime(Math.max(20, f1), t0 + dur)
    g.gain.setValueAtTime(0.0001, t0)
    g.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak), t0 + attack)
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur)
    osc.connect(g)
    g.connect(dest ?? this.sfxBus!)
    osc.start(t0)
    osc.stop(t0 + dur + 0.02)
  }

  private noiseBurst(
    dur: number,
    peak: number,
    filterType: BiquadFilterType,
    f0: number,
    f1: number,
    dest?: AudioNode,
    when?: number,
  ): void {
    const ctx = this.ctx
    if (!ctx || !this.noise) return
    const t0 = when ?? this.t()
    const src = ctx.createBufferSource()
    src.buffer = this.noise
    src.loop = true
    const filt = ctx.createBiquadFilter()
    filt.type = filterType
    filt.frequency.setValueAtTime(f0, t0)
    if (f1 !== f0) filt.frequency.exponentialRampToValueAtTime(Math.max(40, f1), t0 + dur)
    filt.Q.value = 1.1
    const g = ctx.createGain()
    g.gain.setValueAtTime(0.0001, t0)
    g.gain.exponentialRampToValueAtTime(Math.max(0.0002, peak), t0 + 0.006)
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur)
    src.connect(filt)
    filt.connect(g)
    g.connect(dest ?? this.sfxBus!)
    src.start(t0)
    src.stop(t0 + dur + 0.02)
  }

  // ------------------------------------------------------------------- sfx

  sfx(name: string): void {
    if (!this.ready || !this.ctx || this.muted || this.volume <= 0) return
    if (this.ctx.state !== 'running') return
    try {
      switch (name as SfxName) {
        case 'swing':
          this.noiseBurst(0.14, 0.16, 'bandpass', 1800, 520)
          break
        case 'shoot':
          this.tone('square', 900, 380, 0.09, 0.1)
          this.noiseBurst(0.06, 0.07, 'highpass', 2400, 1400)
          break
        case 'hit':
          this.tone('square', 210, 90, 0.08, 0.16)
          this.noiseBurst(0.06, 0.13, 'lowpass', 1400, 500)
          break
        case 'kill':
          this.tone('triangle', 320, 70, 0.22, 0.2)
          this.noiseBurst(0.2, 0.16, 'lowpass', 1100, 220)
          break
        case 'generator-die':
          this.tone('sawtooth', 180, 40, 0.55, 0.22)
          this.noiseBurst(0.5, 0.24, 'lowpass', 900, 120)
          this.tone('square', 90, 45, 0.4, 0.12, 0.02)
          break
        case 'pickup':
          this.tone('triangle', 780, 1180, 0.09, 0.13)
          this.tone('triangle', 1180, 1560, 0.09, 0.09, 0.005, undefined, this.t() + 0.06)
          break
        case 'door':
          this.noiseBurst(0.7, 0.2, 'lowpass', 500, 90)
          this.tone('sawtooth', 120, 60, 0.6, 0.1, 0.05)
          break
        case 'player-hurt':
          this.tone('sawtooth', 300, 120, 0.26, 0.22, 0.004)
          this.noiseBurst(0.2, 0.14, 'bandpass', 900, 300)
          break
        case 'dash':
          this.noiseBurst(0.18, 0.12, 'bandpass', 700, 2600)
          break
        case 'potion':
          this.tone('sine', 220, 1400, 0.35, 0.2, 0.01)
          this.noiseBurst(0.45, 0.22, 'lowpass', 3000, 300)
          break
        case 'telegraph':
          this.tone('square', 620, 900, 0.16, 0.1, 0.01)
          break
        case 'death':
          this.tone('sawtooth', 260, 40, 1.2, 0.26, 0.02)
          this.noiseBurst(1.1, 0.18, 'lowpass', 700, 60)
          break
        case 'victory': {
          // boss-down fanfare: a rising arpeggio into a held octave chord, with
          // a shimmer under the hold. Bright on purpose — it plays over the
          // boss's own death rumble and has to read as triumph, not more damage.
          const t0 = this.t()
          this.tone('square', 523, 523, 0.16, 0.11, 0.008, undefined, t0)
          this.tone('square', 659, 659, 0.16, 0.11, 0.008, undefined, t0 + 0.14)
          this.tone('square', 784, 784, 0.18, 0.12, 0.008, undefined, t0 + 0.28)
          this.tone('square', 1046, 1046, 0.7, 0.13, 0.01, undefined, t0 + 0.44)
          this.tone('triangle', 523, 523, 0.8, 0.1, 0.015, undefined, t0 + 0.44)
          this.tone('triangle', 784, 784, 0.8, 0.07, 0.015, undefined, t0 + 0.44)
          this.noiseBurst(0.9, 0.05, 'highpass', 5200, 7800, undefined, t0 + 0.44)
          break
        }
        case 'ui':
          this.tone('square', 520, 700, 0.06, 0.08)
          break
        default:
          this.tone('square', 440, 440, 0.05, 0.06)
      }
    } catch {
      /* never let a sound crash a frame */
    }
  }

  // ----------------------------------------------------------------- music

  setMusic(p: MusicParams | null): void {
    this.params = p
    this.musicOn = p != null
    this.step = 0
    this.nextStepTime = this.t() + 0.05
  }

  setCombat(on: boolean): void {
    this.combat = on
  }

  stopMusic(): void {
    this.musicOn = false
    this.params = null
  }

  /** Called once per rendered frame with real (unscaled) seconds. */
  tick(realDt: number): void {
    if (!this.ready || !this.ctx || !this.musicOn || !this.params) return
    if (this.ctx.state !== 'running') return
    const target = this.combat ? 1 : 0
    this.percGain += (target - this.percGain) * Math.min(1, realDt * 1.6)

    const p = this.params
    const stepDur = 60 / Math.max(40, p.tempo) / 4 // 16ths
    const now = this.t()
    if (this.nextStepTime < now) this.nextStepTime = now + 0.02
    let guard = 0
    while (this.nextStepTime < now + 0.25 && guard++ < 64) {
      try {
        this.scheduleStep(this.step, this.nextStepTime, p)
      } catch {
        /* ignore */
      }
      this.step = (this.step + 1) % 64
      this.nextStepTime += stepDur
    }
  }

  private scheduleStep(step: number, when: number, p: MusicParams): void {
    const scale = MODES[p.mode] ?? MODES.aeolian
    const bar = Math.floor(step / 16)
    const s = step % 16
    const music = this.musicBus!

    // bass: root-fifth pulse, the spine
    if (p.bassLevel > 0 && (s === 0 || s === 6 || s === 8 || s === 11)) {
      const deg = s === 6 ? 4 : s === 11 ? 2 : 0
      const n = p.root - 12 + scale[deg % scale.length]
      this.tone('triangle', midiToFreq(n), midiToFreq(n) * 0.995, 0.28, 0.14 * p.bassLevel, 0.008, music, when)
    }

    // pad: slow chord bed, changes each bar
    if (p.padLevel > 0 && s === 0) {
      const roots = [0, 5, 3, 4]
      const base = scale[roots[bar % roots.length] % scale.length]
      const dur = (60 / p.tempo) * 4
      for (const iv of [0, 3, 7]) {
        const n = p.root + base + (iv === 3 ? scale[2] - 3 : 0) + iv
        this.tone('sine', midiToFreq(n), midiToFreq(n), dur, 0.05 * p.padLevel, 0.35, music, when)
      }
    }

    // sparse melodic motes — character hint decides how busy
    const busy = p.character === 'court' ? 3 : p.character === 'depths' ? 7 : 5
    if (p.padLevel > 0 && s % busy === 2 && (bar % 2 === 1 || p.character === 'drips')) {
      const deg = (step * 5) % scale.length
      const n = p.root + 12 + scale[deg]
      this.tone('triangle', midiToFreq(n), midiToFreq(n), 0.18, 0.035 * p.padLevel, 0.01, music, when)
    }

    // percussion — gated by combat state
    const perc = p.percLevel * this.percGain
    if (perc > 0.02) {
      if (s === 0 || s === 8 || (s === 11 && bar % 2 === 1)) {
        this.tone('sine', 150, 46, 0.16, 0.3 * perc, 0.003, music, when)
      }
      if (s === 4 || s === 12) {
        this.noiseBurst(0.13, 0.13 * perc, 'bandpass', 1800, 900, music, when)
      }
      if (s % 2 === 1) {
        this.noiseBurst(0.035, 0.05 * perc, 'highpass', 6500, 6500, music, when)
      }
    }
  }
}

export const audio = new AudioEngine()
